import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Avatar, CircularProgress, Button } from '@mui/material';
import { Phone, Mail, AlertCircle, Ban, PawPrint, MapPin, Clock, CheckCircle2, AlertTriangle, MessageCircle, Heart, Pill, ChevronDown, ChevronUp, AlertOctagon, Navigation } from 'lucide-react';
import { API_URL } from '../config/api';
import appStoreBadge from '../assets/images/badge-app-store.svg';
import googlePlayBadge from '../assets/images/badge-google-play.svg';

const ff = "'Poppins', sans-serif";

// App store links come from the admin-editable app config (/api/app/config).
// These defaults are used until that fetch resolves (and match the backend
// DEFAULT_APP_CONFIG), so the download badges are never dead links.
const DEFAULT_APP_STORE_URL = 'https://apps.apple.com/us/app/oktreat-pet-sitting-boarding/id6479255523';
const DEFAULT_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.oktreat.app';

const Card = ({ children }) => (
  <Box sx={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 2,
    background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
  }}>
    <Box sx={{
      width: '100%',
      maxWidth: 440,
      background: '#FFF',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(50, 166, 216, 0.15)',
      overflow: 'hidden',
    }}>
      {children}
    </Box>
  </Box>
);

const Header = () => (
  <Box sx={{
    background: 'linear-gradient(135deg, #32A6D8 0%, #2B8FB8 100%)',
    color: '#FFF',
    textAlign: 'center',
    py: 3,
    px: 3,
  }}>
    <Box sx={{ fontFamily: ff, fontSize: 24, fontWeight: 700, letterSpacing: 0.5 }}>OkTreat</Box>
    <Box sx={{ fontFamily: ff, fontSize: 12, opacity: 0.9, mt: 0.5 }}>Pet Tag Lookup</Box>
  </Box>
);

// Official Apple / Google Play badges (SVG, 120x40). Each is wrapped in a
// link that lifts slightly on hover. Store URLs come from the constants above.
const StoreBadge = ({ href, src, alt }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={alt}
    sx={{
      display: 'inline-flex',
      borderRadius: '8px',
      transition: 'transform .15s ease, box-shadow .15s ease',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 18px rgba(0,0,0,0.22)' },
      '&:focus-visible': { outline: '2px solid #32A6D8', outlineOffset: '3px' },
    }}
  >
    <Box component="img" src={src} alt={alt} sx={{ height: 46, width: 'auto', display: 'block' }} />
  </Box>
);

// "Get the app" call-to-action, shown to public scanners so a finder can
// grab the app. Store URLs are passed in from the fetched app config; a badge
// is omitted when its URL is empty so we never render a dead link.
const AppDownload = ({ appStoreUrl, playStoreUrl }) => {
  const hasApple = !!(appStoreUrl || '').trim();
  const hasPlay = !!(playStoreUrl || '').trim();
  if (!hasApple && !hasPlay) return null;
  return (
    <Box sx={{
      mt: 3,
      p: 3,
      borderRadius: '16px',
      background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
      border: '1px solid #D6EEFB',
      textAlign: 'center',
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '14px',
        background: 'linear-gradient(135deg, #32A6D8 0%, #2B8FB8 100%)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 6px 16px rgba(50,166,216,0.3)',
      }}>
        <PawPrint size={22} color="#FFF" />
      </Box>
      <Box sx={{ fontFamily: ff, fontSize: 16, fontWeight: 700, color: '#1A202C', mt: 1.5 }}>
        Get the OkTreat app
      </Box>
      <Box sx={{ fontFamily: ff, fontSize: 12.5, color: '#5B6B7B', lineHeight: 1.6, mt: 0.5, mb: 2.5, maxWidth: 320, mx: 'auto' }}>
        Order smart tags, get instant scan alerts, and reunite lost pets faster — all from your phone.
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {hasApple && <StoreBadge href={appStoreUrl} src={appStoreBadge} alt="Download on the App Store" />}
        {hasPlay && <StoreBadge href={playStoreUrl} src={googlePlayBadge} alt="Get it on Google Play" />}
      </Box>
    </Box>
  );
};

const InfoMessage = ({ icon, title, message, color = '#32A6D8', storeLinks }) => (
  <Card>
    <Header />
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Box sx={{
        width: 64, height: 64, borderRadius: '50%',
        background: `${color}22`, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mx: 'auto', mb: 2,
      }}>
        {icon}
      </Box>
      <Box sx={{ fontFamily: ff, fontSize: 18, fontWeight: 600, color: '#1A202C', mb: 1 }}>{title}</Box>
      <Box sx={{ fontFamily: ff, fontSize: 14, color: '#718096', lineHeight: 1.6 }}>{message}</Box>
      <AppDownload appStoreUrl={storeLinks?.appStoreUrl} playStoreUrl={storeLinks?.playStoreUrl} />
    </Box>
  </Card>
);

export default function PublicTagPage() {
  const { code } = useParams();
  // Pet data lookup — deferred until the visitor grants geolocation.
  const [state, setState] = useState({ loading: false, pet: null, error: null, reason: null });
  // "In case of emergency" expandable — collapsed by default so a casual
  // scanner sees the pet + owner CTA first. Auto-expands when the pet is
  // reported missing (finder needs the medical context immediately).
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  // Browser geolocation gate. Product decision: pet contact info stays
  // hidden until the visitor shares location so the owner's scan-alert
  // email includes where the pet was found.
  //   status: 'checking' | 'granted' | 'denied' | 'unsupported' | 'error'
  const [locState, setLocState] = useState({ status: 'checking', coords: null, message: '' });
  // Store links from the admin-editable app config. Seeded with defaults so the
  // download badges work before/if the config fetch fails; an admin-cleared
  // value ('') hides that badge.
  const [storeLinks, setStoreLinks] = useState({
    appStoreUrl: DEFAULT_APP_STORE_URL,
    playStoreUrl: DEFAULT_PLAY_STORE_URL,
  });

  const requestGeolocation = () => {
    if (!navigator.geolocation) {
      setLocState({ status: 'unsupported', coords: null, message: 'Your browser does not support geolocation. Try opening this link in a modern browser.' });
      return;
    }
    setLocState((s) => ({ ...s, status: 'checking' }));
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocState({ status: 'granted', coords: { latitude: pos.coords.latitude, longitude: pos.coords.longitude }, message: '' }),
      (err) => {
        // 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT
        const denied = err && err.code === 1;
        setLocState({
          status: denied ? 'denied' : 'error',
          coords: null,
          message: denied
            ? 'Please allow location access in your browser so we can tell the owner where their pet was scanned.'
            : 'We could not read your location. Please make sure location services are turned on and try again.',
        });
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 }
    );
  };

  useEffect(() => {
    requestGeolocation();
  }, []);

  // Fetch the admin-editable store links once on mount. Fail-open: any error
  // keeps the seeded defaults so the download CTA never breaks.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/app/config`);
        const json = await res.json();
        const cfg = json?.data;
        if (!cancelled && cfg && typeof cfg === 'object') {
          setStoreLinks({
            appStoreUrl: cfg.appStoreUrl ?? DEFAULT_APP_STORE_URL,
            playStoreUrl: cfg.playStoreUrl ?? DEFAULT_PLAY_STORE_URL,
          });
        }
      } catch (e) {
        // keep defaults
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Once location is granted, fetch pet data AND record the scan so the
  // owner gets an email with coordinates. Scan record is fire-and-forget.
  useEffect(() => {
    if (locState.status !== 'granted' || !locState.coords) return;
    let cancelled = false;
    const load = async () => {
      setState({ loading: true, pet: null, error: null, reason: null });
      try {
        const res = await fetch(`${API_URL}/pets/qr/${encodeURIComponent(code)}`);
        const json = await res.json();
        if (cancelled) return;
        if (res.ok && json.success) {
          setState({ loading: false, pet: json.data.pet, error: null, reason: null });
          // Record the anonymous scan so the owner's email fires with coords.
          fetch(`${API_URL}/scans`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              qrCode: code,
              latitude: locState.coords.latitude,
              longitude: locState.coords.longitude,
            }),
          }).catch(() => {});
        } else {
          setState({ loading: false, pet: null, error: json.message || 'Lookup failed', reason: json.reason || null });
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, pet: null, error: 'Network error. Please try again.', reason: null });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [code, locState.status, locState.coords]);

  // Location gate — first hurdle any visitor sees. Hides all pet info
  // until they either grant location or reload after enabling.
  if (locState.status !== 'granted') {
    if (locState.status === 'checking') {
      return (
        <Card>
          <Header />
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#32A6D8' }} />
            <Box sx={{ fontFamily: ff, fontSize: 13, color: '#718096', mt: 2 }}>Checking your location…</Box>
          </Box>
        </Card>
      );
    }
    return (
      <Card>
        <Header />
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#FFF1EC', color: '#B0211A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 2,
          }}>
            <Navigation size={32} />
          </Box>
          <Box sx={{ fontFamily: ff, fontSize: 18, fontWeight: 700, color: '#1A202C', mb: 1 }}>
            Location required
          </Box>
          <Box sx={{ fontFamily: ff, fontSize: 14, color: '#4B5563', lineHeight: 1.6, mb: 3 }}>
            {locState.message}
          </Box>
          <Button
            variant="contained"
            onClick={requestGeolocation}
            sx={{
              fontFamily: ff, textTransform: 'none',
              fontSize: 15, fontWeight: 700, py: 1.5, px: 4,
              borderRadius: '12px',
              background: '#32A6D8',
              boxShadow: '0 4px 12px rgba(50, 166, 216, 0.25)',
              '&:hover': { background: '#2B8FB8' },
            }}
          >
            Enable location
          </Button>
          <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0', mt: 3, lineHeight: 1.5 }}>
            If you already denied the prompt, tap the lock icon in your browser's address bar to change it and reload this page.
          </Box>
        </Box>
      </Card>
    );
  }

  if (state.loading) {
    return (
      <Card>
        <Header />
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#32A6D8' }} />
          <Box sx={{ fontFamily: ff, fontSize: 13, color: '#718096', mt: 2 }}>Looking up tag...</Box>
        </Box>
      </Card>
    );
  }

  if (state.reason === 'DEACTIVATED') {
    return <InfoMessage icon={<Ban size={32} />} title="Tag Deactivated" message="This QR tag has been deactivated and is no longer in use." color="#E53E3E" storeLinks={storeLinks} />;
  }
  if (state.reason === 'NOT_LINKED') {
    return <InfoMessage icon={<AlertCircle size={32} />} title="Tag Not Linked" message="This tag hasn't been linked to a pet yet. Own this tag? Get the OkTreat app, then scan it from inside the app to add your pet." color="#DD6B20" storeLinks={storeLinks} />;
  }
  if (state.reason === 'NOT_FOUND' || state.reason === 'INVALID_FORMAT' || !state.pet) {
    return <InfoMessage icon={<AlertCircle size={32} />} title="Tag Not Found" message={state.error || 'No pet is associated with this tag.'} color="#A0AEC0" storeLinks={storeLinks} />;
  }

  const pet = state.pet;
  const owner = pet.user;
  const missing = pet.isMissing && pet.missingReport ? pet.missingReport : null;
  const recentlySafe = !pet.isMissing && pet.recentlySafe;
  const primaryPhone = missing?.contactPhone || owner?.phone;
  const waPhone = (primaryPhone || '').replace(/[^0-9]/g, '');
  const waMessage = encodeURIComponent(
    `Hi! I found ${pet.name} (matched via OkTreat QR tag). Please get in touch.`
  );

  // Normalize any JSON field that could be array-or-string legacy data.
  const asList = (m) => {
    if (!m) return [];
    if (Array.isArray(m)) return m.filter(Boolean);
    if (typeof m === 'string' && m.trim()) return [m.trim()];
    return [];
  };
  const medications = asList(pet.medications);
  const foodAllergies = asList(pet.foodAllergies);
  const medAllergies = asList(pet.medicationAllergies);

  // Combined vet contact — falls back to legacy free-text field.
  const vetParts = [pet.vetName, pet.vetPhone, pet.vetAddress].filter(Boolean);
  const vetDisplay = vetParts.length ? vetParts.join(' · ') : (pet.veterinaryInfo || '');

  const healthRows = [
    foodAllergies.length > 0 && { label: 'Food allergies', value: foodAllergies.join(', '), critical: true, icon: <AlertOctagon size={14} /> },
    medAllergies.length > 0 && { label: 'Medication allergies', value: medAllergies.join(', '), critical: true, icon: <AlertOctagon size={14} /> },
    medications.length > 0 && { label: 'Medications', value: medications.join(', '), critical: true, icon: <Pill size={14} /> },
    pet.additionalInstructions && { label: 'Special needs', value: pet.additionalInstructions, critical: true },
    pet.feedingSchedule && { label: 'Feeding', value: pet.feedingSchedule },
    pet.microchipped && { label: 'Microchipped', value: pet.microchipped },
    pet.spayedNeutered && { label: 'Spayed / Neutered', value: pet.spayedNeutered },
    pet.vetPhone && { label: 'Vet phone', value: pet.vetPhone, tel: pet.vetPhone },
    vetDisplay && !pet.vetPhone && { label: 'Vet', value: vetDisplay },
    pet.vetName && pet.vetPhone && { label: 'Vet clinic', value: [pet.vetName, pet.vetAddress].filter(Boolean).join(' · ') || pet.vetName },
  ].filter(Boolean);

  // If the pet is actively missing, expose the medical info immediately —
  // a finder shouldn't have to hunt for allergies while calling the owner.
  const emergencyExpanded = emergencyOpen || !!missing;
  const lastSeenWhen = missing?.lastSeenAt ? new Date(missing.lastSeenAt) : null;
  const lastSeenAgo = lastSeenWhen
    ? (() => {
        const diff = Math.max(0, Date.now() - lastSeenWhen.getTime());
        const h = Math.floor(diff / (1000 * 60 * 60));
        if (h < 1) return 'less than an hour ago';
        if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
        const d = Math.floor(h / 24);
        return `${d} day${d === 1 ? '' : 's'} ago`;
      })()
    : null;

  return (
    <Card>
      <Header />

      {/* Emergency banner for missing pets */}
      {missing && (
        <Box sx={{
          background: 'linear-gradient(135deg, #D93025 0%, #B0211A 100%)',
          color: '#FFF',
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
          <AlertTriangle size={24} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ fontFamily: ff, fontSize: 16, fontWeight: 700, letterSpacing: 0.3 }}>
              THIS PET IS MISSING
            </Box>
            <Box sx={{ fontFamily: ff, fontSize: 12, opacity: 0.95, mt: 0.25 }}>
              Please contact the owner immediately if you've seen {pet.name}.
            </Box>
          </Box>
        </Box>
      )}

      {/* Recently safe banner — 24h after mark-safe */}
      {recentlySafe && (
        <Box sx={{
          background: 'linear-gradient(135deg, #1F9E5C 0%, #157A45 100%)',
          color: '#FFF',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
          <CheckCircle2 size={22} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 700 }}>
              Recently safe!
            </Box>
            <Box sx={{ fontFamily: ff, fontSize: 12, opacity: 0.95, mt: 0.25 }}>
              {pet.name} was found within the last 24 hours.
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Avatar
            src={pet.photoUrl}
            sx={{ width: 120, height: 120, mx: 'auto', mb: 2, border: '4px solid #E0F2FE', background: '#F0F9FF' }}
          >
            <PawPrint size={48} color="#32A6D8" />
          </Avatar>
          <Box sx={{ fontFamily: ff, fontSize: 26, fontWeight: 700, color: '#1A202C' }}>{pet.name}</Box>
          <Box sx={{ fontFamily: ff, fontSize: 14, color: '#718096', mt: 0.5 }}>
            {[pet.type, pet.breed].filter(Boolean).join(' · ')}
          </Box>
        </Box>

        {/* Last-seen details for missing pets */}
        {missing && (
          <Box sx={{
            background: '#FFF1EC',
            border: '1px solid #FFD4C2',
            borderRadius: '12px',
            p: 2.5,
            mb: 2,
          }}>
            <Box sx={{ fontFamily: ff, fontSize: 11, fontWeight: 700, color: '#B0211A', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
              Last Seen
            </Box>
            {lastSeenWhen && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Clock size={14} color="#B0211A" style={{ marginTop: 3 }} />
                <Box sx={{ fontFamily: ff, fontSize: 13, color: '#1A202C' }}>
                  {lastSeenWhen.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  <Box component="span" sx={{ color: '#718096', ml: 0.5 }}>({lastSeenAgo})</Box>
                </Box>
              </Box>
            )}
            {missing.lastSeenAddress && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <MapPin size={14} color="#B0211A" style={{ marginTop: 3 }} />
                <Box sx={{ fontFamily: ff, fontSize: 13, color: '#1A202C', lineHeight: 1.5 }}>
                  {missing.lastSeenAddress}
                </Box>
              </Box>
            )}
            {missing.notes && (
              <Box sx={{
                fontFamily: ff,
                fontSize: 12,
                color: '#4B5563',
                background: '#FFFFFF',
                borderRadius: '8px',
                p: 1.5,
                mt: 1,
                lineHeight: 1.5,
              }}>
                {missing.notes}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{
          background: missing ? '#FFF8F6' : '#F7FAFC',
          border: missing ? '1px solid #FFD4C2' : 'none',
          borderRadius: '12px',
          p: 2.5,
          mb: 3,
        }}>
          <Box sx={{
            fontFamily: ff,
            fontSize: 11,
            fontWeight: 700,
            color: missing ? '#B0211A' : '#A0AEC0',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            mb: 1,
          }}>
            {missing ? 'Call Owner Now' : 'If Found, Contact'}
          </Box>
          <Box sx={{ fontFamily: ff, fontSize: 16, fontWeight: 600, color: '#1A202C' }}>
            {owner?.fullName || 'Owner'}
          </Box>
          {primaryPhone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Phone size={14} color={missing ? '#D93025' : '#32A6D8'} />
              <Box sx={{ fontFamily: ff, fontSize: 14, color: missing ? '#D93025' : '#32A6D8', wordBreak: 'break-all', fontWeight: missing ? 600 : 400 }}>
                {primaryPhone}
              </Box>
            </Box>
          )}
          {owner?.email && !missing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Mail size={14} color="#32A6D8" />
              <Box sx={{ fontFamily: ff, fontSize: 14, color: '#32A6D8', wordBreak: 'break-all' }}>{owner.email}</Box>
            </Box>
          )}
          {!primaryPhone && !owner?.email && (
            <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0', fontStyle: 'italic', mt: 0.5, lineHeight: 1.5 }}>
              The owner hasn't shared any contact info on this tag. You can
              still keep the pet safe — leave a comment or take them to a
              local vet who can read the microchip if there is one.
            </Box>
          )}
        </Box>

        {(primaryPhone || owner?.email) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {primaryPhone && (
                <Button
                  fullWidth
                  variant="contained"
                  href={`tel:${primaryPhone}`}
                  startIcon={<Phone size={missing ? 22 : 18} />}
                  sx={{
                    fontFamily: ff, textTransform: 'none',
                    fontSize: missing ? 17 : 15,
                    fontWeight: missing ? 700 : 600,
                    py: missing ? 2 : 1.5,
                    borderRadius: '12px',
                    background: missing ? '#D93025' : '#32A6D8',
                    boxShadow: missing ? '0 4px 12px rgba(217, 48, 37, 0.35)' : 'none',
                    '&:hover': { background: missing ? '#B0211A' : '#2B8FB8' },
                  }}
                >
                  {missing ? 'CALL OWNER NOW' : 'Call'}
                </Button>
              )}
              {owner?.email && !missing && (
                <Button
                  fullWidth
                  variant={primaryPhone ? 'outlined' : 'contained'}
                  href={`mailto:${owner.email}?subject=${encodeURIComponent(`Found your pet ${pet.name}`)}`}
                  startIcon={<Mail size={18} />}
                  sx={primaryPhone ? {
                    fontFamily: ff, textTransform: 'none', fontSize: 15, fontWeight: 600,
                    py: 1.5, borderRadius: '12px',
                    color: '#32A6D8', borderColor: '#32A6D8',
                    '&:hover': { borderColor: '#2B8FB8', background: '#F0F9FF' },
                  } : {
                    fontFamily: ff, textTransform: 'none', fontSize: 15, fontWeight: 600,
                    py: 1.5, borderRadius: '12px',
                    background: '#32A6D8',
                    '&:hover': { background: '#2B8FB8' },
                  }}
                >
                  Email
                </Button>
              )}
            </Box>
            {waPhone ? (
              <Button
                fullWidth
                variant="contained"
                href={`https://wa.me/${waPhone}?text=${waMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<MessageCircle size={18} />}
                sx={{
                  fontFamily: ff, textTransform: 'none',
                  fontSize: 15, fontWeight: 600, py: 1.5,
                  borderRadius: '12px',
                  background: '#25D366',
                  boxShadow: '0 4px 12px rgba(37, 211, 102, 0.25)',
                  '&:hover': { background: '#1FAE53' },
                }}
              >
                WhatsApp Owner
              </Button>
            ) : (
              <Box sx={{
                fontFamily: ff,
                fontSize: 12,
                color: '#A0AEC0',
                fontStyle: 'italic',
                textAlign: 'center',
                py: 1,
              }}>
                The owner hasn't shared a phone number, so WhatsApp isn't
                available for this pet. Use the email button above instead.
              </Box>
            )}
          </Box>
        )}

        {/* In case of emergency — collapsed by default so a casual scanner
            sees the pet + owner CTA first. Auto-expanded when the pet is
            actively missing (the finder needs medical context immediately). */}
        <Box sx={{
          mt: 3,
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
          <Box
            onClick={() => setEmergencyOpen((v) => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 2.5,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { background: '#F9FAFB' },
            }}
          >
            <Heart size={16} color="#D93025" />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 700, color: '#1A202C' }}>
                In case of emergency
              </Box>
              <Box sx={{ fontFamily: ff, fontSize: 11, color: '#718096', mt: 0.25 }}>
                Allergies, medications and vet contact for {pet.name}
              </Box>
            </Box>
            {emergencyExpanded ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
          </Box>
          {emergencyExpanded && (
            <Box sx={{ px: 2.5, pb: 2.5, borderTop: '1px solid #F3F4F6' }}>
              {healthRows.length > 0 ? (
                healthRows.map((row) => (
                  <Box key={row.label} sx={{
                    py: 1,
                    borderBottom: '1px solid #F3F4F6',
                    '&:last-child': { borderBottom: 'none' },
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontFamily: ff,
                      fontSize: 10,
                      fontWeight: 700,
                      color: row.critical ? '#B0211A' : '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                      mb: 0.5,
                    }}>
                      {row.icon}
                      {row.label}
                    </Box>
                    {row.tel ? (
                      <Box
                        component="a"
                        href={`tel:${row.tel}`}
                        sx={{
                          fontFamily: ff,
                          fontSize: 13,
                          color: '#32A6D8',
                          textDecoration: 'none',
                          lineHeight: 1.5,
                          fontWeight: 600,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {row.value}
                      </Box>
                    ) : (
                      <Box sx={{
                        fontFamily: ff,
                        fontSize: 13,
                        color: '#1F2937',
                        lineHeight: 1.5,
                        fontWeight: row.critical ? 600 : 400,
                      }}>
                        {row.value}
                      </Box>
                    )}
                  </Box>
                ))
              ) : (
                <Box sx={{
                  fontFamily: ff,
                  fontSize: 12,
                  color: '#718096',
                  fontStyle: 'italic',
                  lineHeight: 1.6,
                  pt: 1.5,
                }}>
                  The owner hasn't shared any health details for this pet yet. If
                  the pet seems unwell or you notice anything concerning, please
                  contact the owner directly using the buttons above.
                </Box>
              )}
            </Box>
          )}
        </Box>

        <AppDownload appStoreUrl={storeLinks.appStoreUrl} playStoreUrl={storeLinks.playStoreUrl} />

        <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0', textAlign: 'center', mt: 3 }}>
          {missing
            ? 'Calling the owner immediately is the fastest way to reunite them with this pet.'
            : 'Thank you for helping reunite this pet with their family'}
        </Box>
      </Box>
    </Card>
  );
}
